import { computed, ref, onUnmounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useConfirmDialogStore } from '@/stores';
import type { ConfirmDialogParam } from '@/types/models/common.d';

type ShowConfirmDialogParam = Omit<ConfirmDialogParam, 'onClose'>;

export const useConfirmDialog = () => {
  const confirmDialogStore = useConfirmDialogStore();
  const { confirmDialogParam } = storeToRefs(confirmDialogStore);

  // このcomposableを呼び出したコンポーネントのマウント状態を追跡
  const isMounted = ref(true);

  // このインスタンス自身が表示中のダイアログの resolve。アンマウント時にまだ残っていれば、
  // そのダイアログを閉じずに放置すると（画面遷移後も居座り、古い状態で確定できてしまう上、
  // ストアの単一スロットを占有し続けて後続の確認も一切出せなくなるため）ここで自分から閉じる。
  let ownResolve: ((isOk: boolean) => void) | null = null;

  onUnmounted(() => {
    isMounted.value = false;

    if (ownResolve) {
      confirmDialogParam.value = undefined;
      const resolve = ownResolve;
      ownResolve = null;
      resolve(false);
    }
  });

  /**
   * 確認ダイアログを表示する
   * @returns ユーザーがポジティブボタンを選択した場合true、ネガティブボタンを選択した場合falseを返すPromise
   */
  const showConfirmDialog = (param: ShowConfirmDialogParam): Promise<boolean> => {
    // アンマウント後は何もしない
    if (!isMounted.value) return Promise.resolve(false);

    // 確認ダイアログはストアが単一スロットのため、アプリ全体で同時に1つしか出せない。
    // 表示中に呼ばれても先行呼び出しの Promise を上書きで握りつぶさないよう、無視して false で解決する。
    if (confirmDialogParam.value !== undefined) return Promise.resolve(false);

    return new Promise<boolean>((resolve) => {
      ownResolve = resolve;
      confirmDialogParam.value = {
        ...param,
        onClose: (isOk: boolean) => {
          confirmDialogParam.value = undefined;
          ownResolve = null;
          resolve(isOk);
        },
      };
    });
  };

  return {
    confirmDialogParam: computed(() => confirmDialogParam.value),
    showConfirmDialog,
  };
};
